import type { ReactNode } from "react";
import { cn } from "ui";
import type { Translator } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";
import { validationMessageKey } from "../util/validation";

export interface IFieldProps {
  /** Field label (already translated). */
  label: string;
  /** The control(s) for this field. */
  children: ReactNode;
  /** Validation code for this field (e.g. `program_name_required`), if invalid. */
  error?: string;
  /** Whether the field is required (shows an asterisk). */
  required?: boolean;
  /** Optional helper text shown under the control when there is no error. */
  hint?: string;
  htmlFor?: string;
  className?: string;
  t?: Translator;
}

/** A labelled form field that renders a translated validation error under its control. */
export function Field({ label, children, error, required, hint, htmlFor, className, t: tOverride }: IFieldProps) {
  const { t } = useRewardsAdmin({ t: tOverride });
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={htmlFor} className="text-body-sm font-medium text-ticketon-dark-gray">
        {label}
        {required && <span className="ml-0.5 text-ticketon-error-text">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-body-sm text-ticketon-error-text" data-testid="field-error">
          {t(validationMessageKey(error))}
        </p>
      ) : (
        hint && <p className="text-body-sm text-gray-400">{hint}</p>
      )}
    </div>
  );
}
